import type { Cart } from "@dransay/api/models";
import { useCallback, useEffect, useRef, useState } from "react";
import { type SubmitOptions, useFetcher } from "react-router";
import { isExpectedCartError } from "~/routes/helpers/is-expected-cart-error";
import { getAccessoryItemIds, getAccessoryQuantity } from "./accessory-lines";
import type { VersionedCart } from "./cart.context.types";
import { nextCartVersion } from "./cart-version-allocator";
import { mergeCartItemsPreservingOrder } from "./merge-cart-items-preserving-order";
import { useFetcherPromise } from "./use-fetcher-promise";

// biome-ignore lint/suspicious/noExplicitAny: FormData values can be any serializable type
type CartFormData = { [k: string]: any };

type QueuedWrite = {
	key: string | null;
	formData: CartFormData;
	options: SubmitOptions;
	waiters: { resolve: () => void; reject: (reason?: unknown) => void }[];
};

/**
 * Identifies writes that set the same line to an absolute quantity, so a
 * queued one can be replaced by a later one. Anything else is never merged.
 */
const getCartWriteKey = (
	formData: CartFormData,
	action: string | undefined,
): string | null => {
	if (action === "/api/cart/remove" && formData?.id !== undefined) {
		return `${action}:${formData.id}`;
	}
	if (action === "/api/cart/add" && typeof formData?.body === "string") {
		try {
			const { vendorId, replaceCart, item } = JSON.parse(formData.body);
			return item?.id === undefined
				? null
				: `${action}:${vendorId}:${Boolean(replaceCart)}:${item.id}`;
		} catch {
			return null;
		}
	}
	return null;
};

/**
 * This hook provides local state management for the cart.
 * It allows updating the cart items and recalculating total price and quantity.
 *
 * @param cart - The current cart state
 * @param setCart - Function to update the cart state
 */
export const useCartOptimisticUpdate = (
	initialCart: VersionedCart,
	{ onError }: { onError?: (error: Error) => void } = {},
) => {
	const [cart, setCart] = useState(initialCart);

	const updateLocalCart = (updatedItems: Cart["items"]) => {
		const totalQuantity = updatedItems.reduce(
			(acc, { quantity }) => acc + quantity,
			0,
		);
		const totalPrice = updatedItems.reduce(
			(acc, { quantity, product }) => acc + (product.price ?? 0) * quantity,
			0,
		);

		setCart((stateCart) => {
			const accessoryItemIds = getAccessoryItemIds(stateCart.fulfillmentGroups);
			const accessoryQuantity = getAccessoryQuantity(
				updatedItems,
				accessoryItemIds,
			);
			// The cart carries no accessory ceiling to subtract from, only the
			// server's remainder, so this moves by the delta instead of recomputing.
			// Clamped like the server: a negative remainder opens the PDP sheet on
			// "cart is full" with nothing staged.
			const accessoryDelta =
				accessoryQuantity -
				getAccessoryQuantity(stateCart.items, accessoryItemIds);

			return {
				...stateCart,
				items: updatedItems,
				remainingAccessoryQuantity: Math.max(
					0,
					stateCart.remainingAccessoryQuantity - accessoryDelta,
				),
				// `totalQuantity` is every line because the basket totals need it, but
				// the monthly quota is the cannabis limit the cart read reports from
				// medication lines alone — so the accessory pieces come back out.
				remainingCapacity:
					stateCart.maxTotalCartQuantity -
					(totalQuantity - accessoryQuantity) -
					stateCart.purchasedQuantityOfCurrentMonth,
				totalPrice,
				totalQuantity,
			};
		});
	};

	const updateLocalItemQuantity = (itemId: string, quantity: number) => {
		const isItemInCart = cart.items.some((item) => item.id === itemId);

		if (isItemInCart) {
			const updatedItems = cart.items.map((item) =>
				item.id === itemId ? { ...item, quantity } : item,
			);
			updateLocalCart(updatedItems);
		}
	};

	const fetcher = useFetcher();
	const queueRef = useRef<QueuedWrite[]>([]);
	const [queuedCount, setQueuedCount] = useState(0);
	const isLoading = fetcher.state === "submitting" || queuedCount > 0;
	const error = fetcher.data?.error?.message;

	// useState lazy initializer runs once on mount; useRef(fn()) would re-evaluate
	// nextCartVersion() on every render even though useRef ignores it after the first.
	const [initialVersion] = useState(() => nextCartVersion());
	const latestVersion = useRef(initialVersion);
	const lastProcessedData = useRef<typeof fetcher.data>(null);

	const syncCartFromUpstream = useCallback((newCart: VersionedCart) => {
		const responseVersion = newCart.version || 0;

		if (responseVersion < latestVersion.current) {
			// If the response version is less than our latest request, ignore it
			return;
		}

		latestVersion.current = responseVersion;
		setCart((current) => ({
			...newCart,
			items: mergeCartItemsPreservingOrder(current.items, newCart.items),
		}));
	}, []);

	const {
		submit: originalSubmit,
		resolve,
		reject,
	} = useFetcherPromise<void, CartFormData>();

	// Cart writes run one at a time. A new submit on the fetcher would abort the
	// write in flight, and a signed-out basket lives only in the cookie each
	// request carries, so the next write must start from the cookie the previous
	// response set. Otherwise the aborted write's line is lost (MKP-2247).
	const sendingRef = useRef(false);
	const sendNext = useCallback(() => {
		if (sendingRef.current || fetcher.state !== "idle") {
			return;
		}
		const next = queueRef.current.shift();
		setQueuedCount(queueRef.current.length);
		if (!next) {
			return;
		}
		sendingRef.current = true;
		originalSubmit(fetcher, next.formData, next.options).then(
			() => {
				for (const waiter of next.waiters) waiter.resolve();
			},
			(reason) => {
				for (const waiter of next.waiters) waiter.reject(reason);
			},
		);
	}, [originalSubmit, fetcher]);

	// Enhanced submit function with version tracking
	const submit = useCallback(
		(formData: CartFormData, options: SubmitOptions) => {
			// The version is taken now, not when the write is sent, so responses
			// to writes queued before it do not overwrite the optimistic state.
			const newVersion = nextCartVersion();
			latestVersion.current = newVersion;

			if (formData) {
				formData.__cartVersion = newVersion;
			}

			return new Promise<void>((resolve, reject) => {
				const key = getCartWriteKey(formData, options.action);
				const last = queueRef.current.at(-1);
				if (key && last?.key === key) {
					// Quantities are absolute, so a later tap on the same line
					// replaces the queued one instead of adding a request. Only the
					// last one: replacing an earlier one would reorder the writes.
					last.formData = formData;
					last.waiters.push({ resolve, reject });
				} else {
					queueRef.current.push({
						key,
						formData,
						options,
						waiters: [{ resolve, reject }],
					});
				}
				setQueuedCount(queueRef.current.length);
				sendNext();
			});
		},
		[sendNext],
	);

	const settleWrite = useCallback(
		(data: typeof fetcher.data) => {
			if (data?.error) {
				// The session was refused (the account is gone): no dialog and no
				// report; the storefront layout ends it on the revalidation that follows.
				if (data.error.code === "UNAUTHORIZED") {
					resolve();
					return;
				}
				const error = new Error(data.error.message);
				// Expected cart errors (e.g. "is not available") are surfaced to the
				// user via `onError` (ErrorDialog). Resolving instead of rejecting
				// avoids an unhandled promise rejection that Sentry's global handler
				// would report as a duplicate issue (MKP-1736). Unexpected errors
				// still reject so callers can react to genuine failures.
				if (isExpectedCartError(error)) {
					resolve();
				} else {
					reject(error);
				}
				onError?.(error);

				// revert to the last known good cart state version to accept inital cart update
				// Not while writes are queued: the revalidation would snap their lines
				// back, and a tap in that window would count from the old quantity.
				if (queueRef.current.length === 0) {
					latestVersion.current = cart.version || 0;
				}
				return;
			}

			resolve();

			// Only update the local cart if the response version is greater than or equal to our latest request
			if (data?.cart) {
				syncCartFromUpstream(data.cart);
			}
		},
		[resolve, reject, onError, cart.version, syncCartFromUpstream],
	);

	// One effect for state and data, so a render that brings both settles the
	// answered write before the next one is sent, and sends only one.
	useEffect(() => {
		if (fetcher.data !== lastProcessedData.current) {
			lastProcessedData.current = fetcher.data;
			sendingRef.current = false;
			settleWrite(fetcher.data);
		}

		if (fetcher.state !== "idle") {
			sendingRef.current = false;
			return;
		}
		sendNext();
	}, [fetcher.state, fetcher.data, settleWrite, sendNext]);

	useEffect(() => {
		syncCartFromUpstream(initialCart);
	}, [initialCart, syncCartFromUpstream]);

	return {
		updateLocalItemQuantity,
		submit,
		syncCartFromUpstream,
		cart,
		isLoading,
		error,
	};
};
